import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { GeometryAngleStepOptions, GeometryQuestionOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { showsGeometryAngleStep } from "./AdvancedSettings.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The two geometry dials (ADR 0049 §1, §6).
 *
 * The question picker is basic - it changes what the sheet asks. The angle
 * step is advanced: it changes how hard the arithmetic is, not what it is
 * about, which is ADR 0023 §2's test. `showsGeometryAngleStep` is imported
 * rather than restated so this component and `advancedSettingsInUse` cannot
 * drift apart (see the `footerGroupOf()` warning in CLAUDE.md).
 *
 * Nothing here sizes a drawing, because the format draws nothing (ADR 0049
 * §2). If a future question type needs a figure, read §4 before adding one.
 */
export function GeometrySettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const t = useTranslation();

    function handleQuestionChange(value: GeometryQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...settings, GeometryQuestion: value });
    }

    function handleAngleStepChange(event) {
        setSettings({ ...settings, GeometryAngleStep: Number(event.target.value) });
    }

    return (
        <div className="geometrySettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.GeometryQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(GeometryQuestionOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            {showsGeometryAngleStep(settings) && (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.angleStepLabel')}</span>
                    </div>
                    <div className="radioButtons" role="radiogroup" aria-label={t('settings.angleStepAria')}>
                        {GeometryAngleStepOptions.map((option) => (
                            <div key={option}>
                                <RadioButton
                                    inputId={`geometryAngleStep-${option}`}
                                    value={option}
                                    checked={settings.GeometryAngleStep === option}
                                    onChange={handleAngleStepChange}
                                />
                                <label htmlFor={`geometryAngleStep-${option}`}>{option}°</label>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default GeometrySettings;
